import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { Toasty } from '@opendoor/atoms/toast';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import {
  IconBath,
  IconBed,
  IconCarGarage,
  IconLink,
  IconRosetteDiscount,
  IconRulerMeasure,
} from '@tabler/icons-react';
import { getShareableLink } from 'components/helpers/links';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import { getPriceCut, isRecentlyListed } from 'components/marketplace/helpers/certaintyPanel';
import { determinePropertyActionability } from 'components/marketplace/helpers/propertyActionability';
import { useAvailableSoonEmailCapture } from 'components/marketplace/helpers/useAvailableSoonEmailCapture';
import { CopyLinkToast } from 'components/marketplace/shared/CopyLinkToast';
import { useObservability } from 'helpers/observability';
import Head from 'next/head';

import { HomeDetails } from '../HomeDetails';
import { PropertyStatus } from '../PropertySection/PropertyStatus';
import { RentalScamBanner } from '../PropertySection/RentalScamBanner';
import { SimilarListings } from '../SimilarListings';
import type { PropertyDetailsType, PropertyEngagement } from '../types';
import { AppDownloadQrCard } from './AppDownloadQrCard';
import { PdpEmailCaptureCard } from './PdpEmailCaptureCard';
import { PdpEngagement } from './PdpEngagement';
import { PdpFavoriteButton } from './PdpFavoriteButton';
import { PdpFloorPlans } from './PdpFloorPlans';
import { PdpGuaranteeCard } from './PdpGuaranteeCard';
import { PdpLocationMap } from './PdpLocationMap';
import { PdpMobileTourBanner } from './PdpMobileTourBanner';
import { PdpPhotoViewer } from './PdpPhotoViewer';
import { PdpPriceCard } from './PdpPriceCard';
import { PdpPriceHistory } from './PdpPriceHistory';
import { PdpSchools } from './PdpSchools';
import { PdpStatusBadge } from './PdpStatusBadge';

import styles from './PdpRefresh.module.css';

type PdpRefreshProps = {
  property: PropertyDetailsType;
  engagement?: PropertyEngagement | null;
  heroOverlay?: ReactNode;
  showTour3d?: boolean;
  preview?: boolean;
};

const COPY_TOAST_DURATION_MS = 1_000;
const SHOW_PRICE_HISTORY = false;

const useLocalSaveCount = (
  property: PropertyDetailsType,
  engagement?: PropertyEngagement | null,
) => {
  const { addressToken, isFavoriteLoading, isLiked, onToggleFavorite: toggleFavorite } = property;
  const serverSaveCount = engagement?.saveCount;
  const [baseline, setBaseline] = useState<{
    addressToken: string;
    isLiked: boolean;
    serverSaveCount: number | null | undefined;
  } | null>(null);

  const onToggleFavorite = useCallback(() => {
    if (!isFavoriteLoading) {
      setBaseline((current) => {
        if (current?.addressToken === addressToken && current.serverSaveCount === serverSaveCount) {
          return current;
        }
        return { addressToken, isLiked, serverSaveCount };
      });
    }
    toggleFavorite();
  }, [addressToken, isFavoriteLoading, isLiked, serverSaveCount, toggleFavorite]);

  const adjustment =
    baseline?.addressToken === addressToken && baseline.serverSaveCount === serverSaveCount
      ? Number(isLiked) - Number(baseline.isLiked)
      : 0;
  return { adjustment, onToggleFavorite };
};

export const PdpRefresh = ({
  property,
  engagement,
  heroOverlay,
  showTour3d = false,
  preview = false,
}: PdpRefreshProps) => {
  const [copyToastMessage, setCopyToastMessage] = useState<string | null>(null);
  const [previewFavorite, setPreviewFavorite] = useState<{
    addressToken: string;
    isLiked: boolean;
  } | null>(null);
  const previewLiked =
    previewFavorite?.addressToken === property.addressToken ? previewFavorite.isLiked : false;
  const togglePreviewFavorite = useCallback(() => {
    setPreviewFavorite((favorite) => ({
      addressToken: property.addressToken,
      isLiked: favorite?.addressToken === property.addressToken ? !favorite.isLiked : true,
    }));
  }, [property.addressToken]);
  const favoriteProperty = useMemo(
    () =>
      preview
        ? {
            ...property,
            isFavoriteLoading: false,
            isLiked: previewLiked,
            onToggleFavorite: togglePreviewFavorite,
          }
        : property,
    [preview, previewLiked, property, togglePreviewFavorite],
  );
  const { adjustment: saveCountAdjustment, onToggleFavorite } = useLocalSaveCount(
    favoriteProperty,
    engagement,
  );
  const propertyWithLocalFavorite = useMemo(
    () => ({ ...favoriteProperty, onToggleFavorite }),
    [favoriteProperty, onToggleFavorite],
  );
  const { trackJourneyEvent } = useObservability();
  const copyListingLink = useCallback(() => {
    trackJourneyEvent('marketplaceProperty', 'share', {
      addressUuid: property.addressToken,
      source: 'copy',
    });
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(getShareableLink(property.slug, property.addressToken));
    }
    setCopyToastMessage('Link copied to clipboard');
  }, [property.addressToken, property.slug, trackJourneyEvent]);
  const closeCopyToast = useCallback(() => setCopyToastMessage(null), []);
  const sidebarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return undefined;
    // Tall panels scroll with the page until their bottom fits in the viewport.
    const observer = new ResizeObserver(() => {
      sidebar.style.setProperty('--pdp-sidebar-height', `${sidebar.offsetHeight}px`);
    });
    observer.observe(sidebar);
    return () => observer.disconnect();
  }, []);
  const address = formatStreetAddress(property.street, property.unitType, property.unitNumber);
  const locality = [[property.city, property.state].filter(Boolean).join(', '), property.zip]
    .filter(Boolean)
    .join(' ');
  const fullAddress = [address, locality].filter(Boolean).join(', ');
  const copyAddress = useCallback(() => {
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(fullAddress);
    }
    setCopyToastMessage('Address copied to clipboard');
  }, [fullAddress]);
  const { isActionable, tourKind, showBuyDirect } = determinePropertyActionability(property);
  const showGuarantee =
    property.listingState === 'ON_THE_MARKET' &&
    isActionable &&
    showBuyDirect &&
    property.shouldShowGuarantee;
  const tourKindOverride =
    preview && property.listingState === 'ON_THE_MARKET' && showBuyDirect && tourKind === 'selfTour'
      ? 'tourWithAgent'
      : undefined;
  const availableSoonEmailCapture = useAvailableSoonEmailCapture(
    property.listingState === 'AVAILABLE_SOON',
  );
  const { isExperimentAssignment, trackExposure } = availableSoonEmailCapture;
  useEffect(() => {
    if (isExperimentAssignment) trackExposure();
  }, [isExperimentAssignment, trackExposure]);
  const similarProperties = property.similarProperties.filter(
    (listing) => listing.listingState !== 'IN_CONTRACT',
  );
  const priceDrop =
    property.listingState === 'ON_THE_MARKET' && isActionable
      ? getPriceCut(property.listingHistory)
      : null;
  const specialRate = property.mortgageOffer?.products.find((product) => product.isPromotionalRate);
  const facts = [
    {
      label: property.bedrooms === 1 ? 'Bed' : 'Beds',
      value: property.bedrooms,
      icon: <IconBed size={18} />,
    },
    {
      label: property.bathrooms === 1 ? 'Bath' : 'Baths',
      value: property.bathrooms,
      icon: <IconBath size={18} />,
    },
    {
      label: 'Sqft',
      value: property.sqFtTotalLiving?.toLocaleString('en-US'),
      icon: <IconRulerMeasure size={18} />,
    },
    {
      label: 'Car garage',
      value: property.garageSpaces,
      icon: <IconCarGarage size={18} />,
    },
  ].filter((fact) => fact.value != null);

  return (
    <>
      <Head>
        <meta name="chrome" content="nointentdetection" key="chrome-intent-detection" />
      </Head>
      <Col className="w-full items-center text-od-content-primary">
        <Col className="w-full max-w-[1440px] md:px-8 md:pt-6 lg:px-[60px]">
          <PdpPhotoViewer
            key={property.addressToken}
            property={propertyWithLocalFavorite}
            heroOverlay={heroOverlay}
            showTour3d={showTour3d}
          />
        </Col>
        <Col className={styles.contentShell}>
          <Box className="grid min-w-0 grid-cols-1 gap-x-20 pt-8 lg:grid-cols-[minmax(0,1fr)_29rem] lg:pt-10">
            <Col className="min-w-0">
              <RentalScamBanner />
              <Row
                role="group"
                aria-label="Listing status and activity"
                className="w-full flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-7 empty:hidden"
              >
                <Row className="flex-wrap items-center gap-x-4 gap-y-3">
                  <PropertyStatus property={property} appearance="refresh" />
                  {isActionable && isRecentlyListed(property.daysOnMarket) && (
                    <PdpStatusBadge label="Newly listed" tone="new" />
                  )}
                  {priceDrop && <PdpStatusBadge label="Price drop" tone="priceDrop" />}
                  {specialRate && (
                    <PdpStatusBadge
                      label={`${specialRate.ratePercent}% special rate`}
                      icon={<IconRosetteDiscount size={16} className="shrink-0" aria-hidden />}
                      tone="priceDrop"
                    />
                  )}
                  <PdpEngagement
                    engagement={engagement}
                    saveCountAdjustment={saveCountAdjustment}
                  />
                </Row>
                <Row
                  role="group"
                  aria-label="Listing actions"
                  className={`${styles.actionGroup} shrink-0 items-center gap-0 rounded-full bg-od-bg-secondary`}
                >
                  <Button
                    variant="secondary"
                    size="md"
                    analyticsName="pdp-refresh-share"
                    aria-label="Copy home link"
                    onClick={copyListingLink}
                    className={`${styles.actionButton} ${styles.linkAction} rounded-full border-0 bg-transparent p-0 !text-od-content-secondary shadow-none transition-colors duration-150 ease-out hover:bg-od-bg-scrim/10 focus-visible:bg-od-bg-scrim/10 active:bg-od-bg-scrim/15`}
                  >
                    <IconLink size={18} stroke={2.2} aria-hidden="true" />
                  </Button>
                  <PdpFavoriteButton
                    key={property.addressToken}
                    isLiked={propertyWithLocalFavorite.isLiked}
                    onToggleFavorite={onToggleFavorite}
                    className={`${styles.actionButton} rounded-full border-0 bg-transparent p-0 !text-od-content-secondary shadow-none transition-colors duration-150 ease-out hover:bg-od-bg-scrim/10 focus-visible:bg-od-bg-scrim/10 active:bg-od-bg-scrim/15`}
                  />
                </Row>
              </Row>
              <Col className="gap-[11px]">
                {property.listingState === 'ON_THE_MARKET' && property.listPrice != null && (
                  <Text as="p" className="text-label-lg text-od-content-secondary">
                    {formatCurrency(property.listPrice)}
                  </Text>
                )}
                <Box className="relative w-fit max-w-full">
                  <Col className="gap-1">
                    <Text
                      as="h1"
                      className="text-balance text-subheader-lg text-od-content-primary md:text-header-md"
                    >
                      {address}
                    </Text>
                    <Text as="p" className="text-body-lg text-od-content-tertiary">
                      {locality}
                    </Text>
                  </Col>
                  <button
                    type="button"
                    aria-label={`Copy address: ${fullAddress}`}
                    disabled={!fullAddress}
                    onClick={copyAddress}
                    className="absolute inset-0 rounded border-0 bg-transparent p-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-od-border-focused-secondary"
                  />
                </Box>
              </Col>
              <Box
                role="group"
                aria-label="Property summary"
                className="grid w-full grid-cols-2 gap-2 pt-8 pb-10 lg:max-w-[504px] lg:grid-cols-[repeat(auto-fit,120px)]"
              >
                {facts.map(({ label, value, icon }) => (
                  <Col
                    key={label}
                    className="w-full min-w-0 gap-3 rounded-lg border border-od-border-primary p-[11px] lg:w-[120px]"
                  >
                    <Box aria-hidden="true" className="text-od-content-inactive-primary">
                      {icon}
                    </Box>
                    <Text className="whitespace-nowrap text-body-md text-od-content-primary">
                      {value} {label.toLowerCase()}
                    </Text>
                  </Col>
                ))}
              </Box>
            </Col>
            <Col className="min-w-0 pb-[60px] lg:row-span-2 lg:col-start-2 lg:row-start-1 lg:pb-[100px]">
              <Col
                ref={sidebarRef}
                className="gap-5 lg:sticky lg:top-[min(6rem,calc(100dvh-var(--pdp-sidebar-height,0px)-1rem))]"
              >
                <Box>
                  <PdpPriceCard
                    key={property.addressToken}
                    availableSoonCaptureRegion={
                      availableSoonEmailCapture.enabled && (
                        <PdpEmailCaptureCard property={property} />
                      )
                    }
                    property={property}
                    onCopyLink={copyListingLink}
                    tourKindOverride={tourKindOverride}
                  />
                </Box>
                {showGuarantee && (
                  <Box className="hidden lg:flex" data-pdp-guarantee-placement="desktop">
                    <PdpGuaranteeCard />
                  </Box>
                )}
              </Col>
            </Col>
            {/* Sections sit 60px apart; the 100px bottom padding is the gap above Similar homes. */}
            <Col className="min-w-0 gap-[60px] pb-7 lg:col-start-1 lg:pb-[100px]">
              <Col className="min-w-0 gap-10">
                <HomeDetails property={property} layout="overview" />
                {showGuarantee && (
                  <Box className="lg:hidden" data-pdp-guarantee-placement="mobile">
                    <PdpGuaranteeCard />
                  </Box>
                )}
                <PdpFloorPlans key={property.addressToken} property={property} />
              </Col>
              <PdpSchools schools={property.schools} schoolDistricts={property.schoolDistricts} />
              {property.latitude != null && property.longitude != null && (
                <Col as="section" aria-label="Explore the neighborhood" className="gap-6">
                  <Text as="h2" className="text-label-lg">
                    Explore the neighborhood
                  </Text>
                  <Box className="h-64 overflow-hidden rounded-xl">
                    <PdpLocationMap latitude={property.latitude} longitude={property.longitude} />
                  </Box>
                </Col>
              )}
              {SHOW_PRICE_HISTORY && (
                <PdpPriceHistory
                  key={property.addressToken}
                  listingHistory={property.listingHistory}
                />
              )}
            </Col>
          </Box>
        </Col>
        {similarProperties.length > 0 && (
          <Col
            as="section"
            aria-label="Similar homes"
            className="w-full items-center bg-od-bg-secondary"
          >
            <Box
              className={`${styles.contentShell} pt-8 ${isActionable ? 'pb-[calc(4.25rem+max(15px,env(safe-area-inset-bottom)))]' : 'pb-8'} min-[1024px]:pb-8 lg:py-[60px]`}
            >
              <SimilarListings
                key={property.addressToken}
                wide
                title={
                  property.city
                    ? `Similar homes in ${property.city}${property.state ? `, ${property.state}` : ''}`
                    : 'Similar homes'
                }
                listings={similarProperties}
              />
            </Box>
          </Col>
        )}
        {isActionable && similarProperties.length === 0 && (
          <Box className="h-28 min-[1024px]:hidden" />
        )}
        {isActionable && (
          <PdpMobileTourBanner
            property={property}
            onCopyLink={copyListingLink}
            tourKindOverride={tourKindOverride}
          />
        )}
      </Col>
      <AppDownloadQrCard key={property.addressToken} />
      <Toasty>
        <CopyLinkToast
          open={copyToastMessage !== null}
          message={copyToastMessage ?? ''}
          onShown={closeCopyToast}
          durationMs={COPY_TOAST_DURATION_MS}
        />
      </Toasty>
    </>
  );
};
